/**
 * Collapse an options list to the first entry per stringified `value`
 * (stable, order-preserving).
 *
 * Duplicate values otherwise produce duplicate React keys and crash React
 * Aria's collection builder. Beyond that crash, the deduped list must be the
 * *single source of truth*: `useAutocompleteNavigation` drives `focusedIndex`
 * and `onSelect` by index into the very same array the popover renders, so a
 * caller must dedupe once and feed the result to both the navigation hook and
 * `AutocompletePopover`. Deduping only inside the popover would leave the
 * caller's indices pointing at the pre-dedup positions — arrow keys and Enter
 * would then highlight one row while committing a different option.
 *
 * Idempotent: calling it on an already-deduped list is a no-op, so the popover
 * can keep it as a defensive net for any direct callers.
 */
// Generic over the array type (`T`) rather than its element so a union of
// homogeneous option arrays — as produced by the branchy `useMemo`s in the
// callers — is accepted without collapsing to a single branch's shape.
export function dedupeOptionsByValue<T extends ReadonlyArray<{ value: string | number | boolean }>>(
  options: T,
): Array<T[number]> {
  const seen = new Set<string>();
  return options.filter((option) => {
    const id = String(option.value);
    return seen.has(id) ? false : (seen.add(id), true);
  });
}
